import { customFetch, makeAjaxForm, submitForm } from '../forms/ajax';
import { debounce } from '../../timing';

export type LinkAction = (searchBar: HTMLElement, resultsContainer: HTMLElement, link: HTMLElement) => any;

// Flag to prevent extra empty searches
let showingEmptyResults = true;

// Wire up physician search bar
export function setupPhysicianSearch(
    searchBar: HTMLElement,
    mainAction: LinkAction = () => null,
    selectAction: LinkAction = () => null,
    moreInfoAction: LinkAction = () => null
)
{
    // Locate the search form
    const searchForm = searchBar.querySelector('.physician-search-form') as HTMLFormElement;

    // Locate the search container and the inputs within it
    const searchResultsId = searchForm.getAttribute('data-results-id');
    const resultsContainer = document.getElementById(searchResultsId);
    const physicianInput = searchForm?.querySelector('[name=physicianQuery]') as HTMLInputElement;

    if (!searchForm || !resultsContainer || !physicianInput)
    {
        console.error('Header search form elements not found');
        return;
    }

    // Wire up canned searches
    for (const cannedSearch of resultsContainer.getElementsByClassName('canned-search') as HTMLCollectionOf<HTMLElement>)
    {
        cannedSearch.addEventListener('click', event => {
            const searchTerm = cannedSearch.getAttribute('data-search');
            physicianInput.value = searchTerm;
            performSearch();
        });
    }

    // Set up AJAX form handling
    makeAjaxForm(
        searchForm,
        formData => onSearchFormSubmit(formData, resultsContainer),
        (response) => onSearchFormResponse(response, searchBar, resultsContainer, mainAction, selectAction, moreInfoAction)
    );

    // Debounced search function that triggers form submission
    const debouncedSearch = debounce(performSearch, 250);

    // Handle input events with debounce
    physicianInput.addEventListener('input', debouncedSearch);

    // Handle focus
    physicianInput.addEventListener('focus', () => showResults(resultsContainer));

    // Handle clicks outside search area
    document.addEventListener('click', (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const headerElement = document.getElementById('page-header-bar');

        if (!headerElement || !resultsContainer)
        {
            return;
        }

        if (!headerElement.contains(target) && !searchBar.contains(target) && !resultsContainer.contains(target))
        {
            clearSearch(searchBar);
            hideResults(resultsContainer);
            clearResults(searchBar, resultsContainer);
        }
    });

    // Handle Escape key
    document.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Escape')
        {
            clearSearch(searchBar);
            hideResults(resultsContainer);
            clearResults(searchBar, resultsContainer);
        }
    });

    function performSearch()
    {
        const physicianQuery = physicianInput.value;

        // Only submit if query has content
        if (physicianQuery)
        {
            submitForm(searchForm);
            showingEmptyResults = false;
        } else
        {
            clearResults(searchBar, resultsContainer);
        }
    }
}

function showResults(resultsContainer: HTMLElement)
{
    resultsContainer.classList.add('visible');
}

function onSearchFormSubmit(formData: FormData, resultsContainer: HTMLElement): boolean
{
    const physicianQuery = formData.get('physicianQuery') as string;

    // Reset instead of searching if the input is empty
    if (physicianQuery.trim() === '')
    {
        return false;
    }

    return true;
}

function onSearchFormResponse(
    response: Response,
    searchBar: HTMLElement,
    resultsContainer: HTMLElement,
    mainAction: LinkAction,
    selectAction: LinkAction,
    moreInfoAction: LinkAction
): boolean
{
    // Determine this is one-phase or two-phase
    const twoPhaseInput = searchBar.querySelector('.two-phase-input') as HTMLInputElement;
    const twoPhase = JSON.parse(twoPhaseInput.value) as boolean;

    if (!resultsContainer)
    {
        console.error('Results container not found');
        return false;
    }

    if (!response.ok)
    {
        console.error('Search request failed:', response.status);
        resultsContainer.innerHTML = `
            <div class="error-message">
                <p>Sorry, there was an error processing your search.</p>
                <p>Please try again later.</p>
            </div>
        `;
        return false;
    }

    response.text().then(html => {
        // Save results in results container
        resultsContainer.innerHTML = html;

        if (twoPhase)
        {
            for (const physicianCard of resultsContainer.querySelectorAll('.physician-card') as NodeListOf<HTMLElement>)
            {
                const physicianLink = physicianCard.querySelector('.physician-link') as HTMLElement;

                // If two-phase, then bind all physician-links to activateTwoPhase
                physicianLink.addEventListener('mousedown', (event) => {
                    event.preventDefault();
                    activateTwoPhase(physicianCard);
                });

                // If two-phase, then bind all select buttons to selectAction
                const selectButton = physicianCard.querySelector('.select-button');
                selectButton.addEventListener('mousedown', (event) => {
                    event.preventDefault();
                    selectAction(searchBar, resultsContainer, physicianLink);
                });

                // If two-phase, then bind all moreInfo buttons to moreInfoAction
                const moreInfoButton = physicianCard.querySelector('.more-info-button');
                moreInfoButton.addEventListener('mousedown', (event) => {
                    event.preventDefault();
                    moreInfoAction(searchBar, resultsContainer, physicianLink);
                });

                // If two-phase, then bind all close-buttons to deactivateTwoPhase
                const closeImg = physicianCard.querySelector('.close-img');
                closeImg.addEventListener('mousedown', (event) => {
                    event.preventDefault();
                    deactivateTwoPhase(physicianCard);
                });
            }
        } else
        {
            // If no two-phase, then bind all physician-links to the mainAction
            for (const physicianLink of resultsContainer.querySelectorAll('.physician-link') as NodeListOf<HTMLElement>)
            {
                physicianLink.addEventListener('mousedown', (event) => {
                    event.preventDefault();
                    mainAction(searchBar, resultsContainer, physicianLink);
                });
            }
        }
    });

    return true;
}

function activateTwoPhase(physicianCard: HTMLElement)
{
    // Ensure that active class is present
    if (!physicianCard.classList.contains('active'))
    {
        physicianCard.classList.add('active');
    }
}

function deactivateTwoPhase(physicianCard: HTMLElement)
{
    // Ensure that active class is not present
    if (physicianCard.classList.contains('active'))
    {
        physicianCard.classList.remove('active');
    }
}

export function clearSearch(searchBar: HTMLElement)
{
    // Locate the search form
    const searchForm = searchBar.querySelector('.physician-search-form') as HTMLFormElement;
    const physicianInput = searchForm.querySelector('[name=physicianQuery]') as HTMLInputElement;

    // Clear out the search
    physicianInput.value = '';
}

export function hideResults(resultsContainer: HTMLElement)
{
    // Hide results
    resultsContainer.classList.remove('visible');
}

export function clearResults(searchBar: HTMLElement, resultsContainer: HTMLElement)
{
    // Don't repeat empty search if already showing empty results
    if (showingEmptyResults)
    {
        return;
    }

    return customFetch(`/physicians/empty-search`)
        .then(response => response.text())
        .then(html => {
            showingEmptyResults = true;
            resultsContainer.innerHTML = html;

            // Locate the search form
            const searchForm = searchBar.querySelector('.physician-search-form') as HTMLFormElement;

            // Locate the search container and the inputs within it
            const physicianInput = searchForm?.querySelector('[name=physicianQuery]') as HTMLInputElement;

            // Wire up canned searches
            for (const cannedSearch of resultsContainer.getElementsByClassName('canned-search') as HTMLCollectionOf<HTMLElement>)
            {
                cannedSearch.addEventListener('click', event => {
                    const searchTerm = cannedSearch.getAttribute('data-search');
                    physicianInput.value = searchTerm;
                    submitForm(searchForm);
                    showingEmptyResults = false;
                });
            }
        });
}
